import React, { useState, useMemo, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import DocumentDetails from './DocumentDetails';
import AuditLog from './AuditLog';
import EditDocument from './EditDocument';
import './OrganizationTree.css';

const OrganizationTree = ({ reviews, enrichedDocuments, onDocumentSelect, useApi = false }) => {
  const [expandedNodes, setExpandedNodes] = useState(new Set());
  const [selectedPath, setSelectedPath] = useState(null);
  const [apiData, setApiData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedDocumentId, setSelectedDocumentId] = useState(null);
  const [selectedAuditDocumentId, setSelectedAuditDocumentId] = useState(null);
  const [selectedEditDocumentId, setSelectedEditDocumentId] = useState(null);
  const { getAuthHeaders } = useAuth();

  // Fetch organizational structure from API if requested
  useEffect(() => {
    if (useApi) {
      const fetchOrganizationData = async () => {
        setLoading(true);
        setError(null);
        try {
          const authHeaders = getAuthHeaders();
          const response = await fetch('/organization/structure', {
            headers: authHeaders
          });

          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          const data = await response.json();
          setApiData(data);
        } catch (err) {
          console.error('Error fetching organization data:', err);
          setError('Failed to load organizational structure');
        } finally {
          setLoading(false);
        }
      };

      fetchOrganizationData();
    }
  }, [useApi]);

  // Helper function to get enriched document data for a review
  const getDocumentData = (review) => {
    if (!enrichedDocuments) return null;
    return enrichedDocuments.find(doc => doc.document_id === review.document_id);
  };

  // Extract agency information from contractor/subcontractor names
  // Since we don't have direct agency data, we'll infer it from the verification process
  const extractAgencyFromContractor = (contractorName, subcontractorName) => {
    // For demo purposes, we'll create logical agency groupings
    // In a real implementation, this would come from the verify_employee_details lookup
    const companyName = contractorName || subcontractorName || 'Unknown Company';

    // Group contractors by common patterns or known agencies
    if (companyName.includes('Steel') || companyName.includes('Frame')) {
      return 'Metro Steel Works Authority';
    } else if (companyName.includes('Electric') || companyName.includes('Solutions')) {
      return 'Regional Electrical Authority';
    } else if (companyName.includes('Construction') || companyName.includes('Concrete') || companyName.includes('Mason')) {
      return 'Metropolitan Construction Authority';
    } else if (companyName.includes('Plumbing') || companyName.includes('Pipes')) {
      return 'Metro Water & Utilities Authority';
    } else if (companyName.includes('Framing') || companyName.includes('Carpenter') || companyName.includes('Doors')) {
      return 'Regional Carpentry Authority';
    } else {
      return 'General Contractors Authority';
    }
  };

  // Build the organizational tree structure
  const organizationTree = useMemo(() => {
    // If using API and data is available, use that
    if (useApi && apiData) {
      return apiData.agencies;
    }

    // Otherwise, build from provided reviews and enriched documents
    if (!reviews || !enrichedDocuments) return {};

    const tree = {};

    reviews.forEach(review => {
      const docData = getDocumentData(review);
      if (!docData) return;

      // Extract organization info
      const agencyName = extractAgencyFromContractor(docData.contractor_name, docData.subcontractor_name);
      const companyName = docData.contractor_name || docData.subcontractor_name || 'Unknown Company';
      const projectName = docData.project_name || 'Unknown Project';
      const payrollPeriod = docData.payroll_start_date && docData.payroll_end_date
        ? `${docData.payroll_start_date} to ${docData.payroll_end_date}`
        : 'Unknown Period';

      // Initialize agency if not exists
      if (!tree[agencyName]) {
        tree[agencyName] = {
          name: agencyName,
          projects: {},
          totalReviews: 0
        };
      }

      // Initialize project if not exists
      if (!tree[agencyName].projects[projectName]) {
        tree[agencyName].projects[projectName] = {
          name: projectName,
          contractors: {},
          totalReviews: 0
        };
      }

      // Initialize contractor if not exists
      if (!tree[agencyName].projects[projectName].contractors[companyName]) {
        tree[agencyName].projects[projectName].contractors[companyName] = {
          name: companyName,
          payrollPeriods: {},
          totalReviews: 0
        };
      }

      // Initialize payroll period if not exists
      if (!tree[agencyName].projects[projectName].contractors[companyName].payrollPeriods[payrollPeriod]) {
        tree[agencyName].projects[projectName].contractors[companyName].payrollPeriods[payrollPeriod] = {
          period: payrollPeriod,
          reviews: [],
          totalReviews: 0
        };
      }

      // Add review to the appropriate location
      tree[agencyName].projects[projectName].contractors[companyName].payrollPeriods[payrollPeriod].reviews.push({
        ...review,
        docData
      });

      // Update counts
      tree[agencyName].totalReviews++;
      tree[agencyName].projects[projectName].totalReviews++;
      tree[agencyName].projects[projectName].contractors[companyName].totalReviews++;
      tree[agencyName].projects[projectName].contractors[companyName].payrollPeriods[payrollPeriod].totalReviews++;
    });

    return tree;
  }, [reviews, enrichedDocuments, useApi, apiData]);

  // Toggle node expansion
  const toggleNode = (path) => {
    setExpandedNodes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(path)) {
        newSet.delete(path);
      } else {
        newSet.add(path);
      }
      return newSet;
    });
  };

  // Handle document selection
  const handleDocumentSelect = (review) => {
    const path = `${extractAgencyFromContractor(review.docData?.contractor_name, review.docData?.subcontractor_name)}/${review.docData?.project_name || 'Unknown Project'}/${review.docData?.contractor_name || review.docData?.subcontractor_name}/${review.docData?.payroll_start_date || 'unknown'}-${review.docData?.payroll_end_date || 'unknown'}/${review.document_id}`;
    setSelectedPath(path);
    if (onDocumentSelect) {
      onDocumentSelect(review);
    }
  };

  // Action button handlers (same as DocumentList.js)
  const handleDocumentClick = (documentId) => {
    setSelectedDocumentId(documentId);
  };

  const handleCloseModal = () => {
    setSelectedDocumentId(null);
  };

  const handleAuditClick = (documentId) => {
    setSelectedAuditDocumentId(documentId);
  };

  const handleCloseAuditModal = () => {
    setSelectedAuditDocumentId(null);
  };

  const handleEditClick = (documentId) => {
    setSelectedEditDocumentId(documentId);
  };

  const handleCloseEditModal = () => {
    setSelectedEditDocumentId(null);
  };

  const handlePreviewClick = async (review) => {
    try {
      // The review object should have a document_id, use that for preview regardless of file_path
      const previewUrl = `/documents/${review.document_id}/preview`;

      // Fetch the file through the API with authentication
      const response = await fetch(previewUrl, {
        method: 'GET',
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Preview error response:', errorText);
        throw new Error(`Preview failed: ${response.status} - ${errorText}`);
      }

      // Create blob and open in new window
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const newWindow = window.open(url, '_blank');

      if (!newWindow) {
        alert('Please allow pop-ups to preview documents');
      } else {
        // Clean up the object URL after a delay to allow the window to load
        setTimeout(() => {
          window.URL.revokeObjectURL(url);
        }, 1000);
      }
    } catch (error) {
      console.error('Error previewing file:', error);
      alert('Error previewing file. Please try again.');
    }
  };

  const handleDownloadClick = async (review) => {
    try {
      // Use API download endpoint for authenticated S3 access (relative URL for frontend proxy)
      const downloadUrl = `/documents/${review.document_id}/download`;

      // Fetch the file through the API
      const response = await fetch(downloadUrl, {
        method: 'GET',
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Download error response:', errorText);
        throw new Error(`Download failed: ${response.status} - ${errorText}`);
      }

      // Get filename from Content-Disposition header or use fallback
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = `document_${review.document_id}`;
      if (contentDisposition) {
        const matches = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (matches && matches[1]) {
          filename = matches[1].replace(/['"]/g, '');
        }
      } else {
        // Fallback to document title
        if (review.document_title) {
          filename = review.document_title + '.pdf';
        }
      }

      // Create blob and download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading file:', error);
      alert('Error downloading file. Please try again.');
    }
  };

  // Get status color for visual indicators
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'approved': return '#28a745';
      case 'processed': return '#28a745';
      case 'processing': return '#ffc107';
      case 'review_required': return '#fd7e14';
      case 'rejected': return '#dc3545';
      case 'failed': return '#dc3545';
      case 'reviewed': return '#17a2b8';
      case 'opened': return '#6c757d';
      case 'pending': return '#6c757d';
      default: return '#6c757d';
    }
  };

  // Render a single review item
  const renderReview = (review, agencyName, projectName, contractorName, payrollPeriod) => {
    const path = `${agencyName}/${projectName}/${contractorName}/${payrollPeriod}/${review.document_id}`;
    const isSelected = selectedPath === path;
    const status = review.current_status || review.new_status;

    return (
      <div
        key={review.review_id}
        className={`tree-review ${isSelected ? 'selected' : ''}`}
      >
        <div className="review-content">
          <div className="review-info">
            <span className="review-title">{review.document_title}</span>
            <span
              className="review-status"
              style={{ backgroundColor: getStatusColor(status) }}
            >
              {status}
            </span>
          </div>
          <div className="review-meta">
            <span className="review-id">#{review.document_id}</span>
            <span className="review-date">{new Date(review.review_date).toLocaleDateString()}</span>
          </div>
        </div>
        <div className="review-actions">
          <div className="action-buttons">
            <button
              className="action-btn preview-btn"
              onClick={(e) => {
                e.stopPropagation();
                handlePreviewClick(review);
              }}
              title="Preview document"
            >
              Preview
            </button>
            <button
              className="action-btn edit-btn"
              onClick={(e) => {
                e.stopPropagation();
                handleEditClick(review.document_id);
              }}
              title="Review document metadata"
            >
              Review
            </button>
            <button
              className="action-btn download-btn"
              onClick={(e) => {
                e.stopPropagation();
                handleDownloadClick(review);
              }}
              title="Download document file"
            >
              Download
            </button>
            <button
              className="action-btn audit-btn"
              onClick={(e) => {
                e.stopPropagation();
                handleAuditClick(review.document_id);
              }}
              title="View status change history"
            >
              Audit
            </button>
            <button
              className="action-btn document-link"
              onClick={(e) => {
                e.stopPropagation();
                handleDocumentSelect(review);
              }}
              title="Go to Document Reviews tab filtered to this document"
              style={{
                backgroundColor: '#007bff',
                color: 'white'
              }}
            >
              Go To
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Render payroll period node
  const renderPayrollPeriod = (periodData, agencyName, projectName, contractorName) => {
    const path = `${agencyName}/${projectName}/${contractorName}/${periodData.period}`;
    const isExpanded = expandedNodes.has(path);

    return (
      <div key={periodData.period} className="tree-period">
        <div
          className="tree-node period-node"
          onClick={() => toggleNode(path)}
        >
          <span className="tree-icon">
            {isExpanded ? '📅' : '📋'}
          </span>
          <span className="node-label">{periodData.period}</span>
          <span className="node-count">({periodData.totalReviews})</span>
          <span className="expand-icon">
            {isExpanded ? '▼' : '▶'}
          </span>
        </div>
        {isExpanded && (
          <div className="tree-children">
            {periodData.reviews.map(review =>
              renderReview(review, agencyName, projectName, contractorName, periodData.period)
            )}
          </div>
        )}
      </div>
    );
  };

  // Render contractor node
  const renderContractor = (contractorData, agencyName, projectName) => {
    const path = `${agencyName}/${projectName}/${contractorData.name}`;
    const isExpanded = expandedNodes.has(path);

    return (
      <div key={contractorData.name} className="tree-contractor">
        <div
          className="tree-node contractor-node"
          onClick={() => toggleNode(path)}
        >
          <span className="tree-icon">
            {isExpanded ? '🏢' : '🏗️'}
          </span>
          <span className="node-label">{contractorData.name}</span>
          <span className="node-count">({contractorData.totalReviews})</span>
          <span className="expand-icon">
            {isExpanded ? '▼' : '▶'}
          </span>
        </div>
        {isExpanded && (
          <div className="tree-children">
            {Object.values(contractorData.payrollPeriods).map(periodData =>
              renderPayrollPeriod(periodData, agencyName, projectName, contractorData.name)
            )}
          </div>
        )}
      </div>
    );
  };

  // Render project node
  const renderProject = (projectData, agencyName) => {
    const path = `${agencyName}/${projectData.name}`;
    const isExpanded = expandedNodes.has(path);

    return (
      <div key={projectData.name} className="tree-project">
        <div
          className="tree-node project-node"
          onClick={() => toggleNode(path)}
        >
          <span className="tree-icon">
            {isExpanded ? '📋' : '📁'}
          </span>
          <span className="node-label">{projectData.name}</span>
          <span className="node-count">({projectData.totalReviews})</span>
          <span className="expand-icon">
            {isExpanded ? '▼' : '▶'}
          </span>
        </div>
        {isExpanded && (
          <div className="tree-children">
            {Object.values(projectData.contractors).map(contractorData =>
              renderContractor(contractorData, agencyName, projectData.name)
            )}
          </div>
        )}
      </div>
    );
  };

  // Render agency node
  const renderAgency = (agencyData) => {
    const path = agencyData.name;
    const isExpanded = expandedNodes.has(path);

    return (
      <div key={agencyData.name} className="tree-agency">
        <div
          className="tree-node agency-node"
          onClick={() => toggleNode(path)}
        >
          <span className="tree-icon">
            {isExpanded ? '🏛️' : '🏛️'}
          </span>
          <span className="node-label">{agencyData.name}</span>
          <span className="node-count">({agencyData.totalReviews})</span>
          <span className="expand-icon">
            {isExpanded ? '▼' : '▶'}
          </span>
        </div>
        {isExpanded && (
          <div className="tree-children">
            {Object.values(agencyData.projects).map(projectData =>
              renderProject(projectData, agencyData.name)
            )}
          </div>
        )}
      </div>
    );
  };

  const totalReviews = Object.values(organizationTree).reduce((sum, agency) => sum + agency.totalReviews, 0);

  // Show loading state
  if (loading) {
    return (
      <div className="organization-tree">
        <div className="tree-header">
          <h3>Organization Structure</h3>
        </div>
        <div className="tree-content">
          <div className="tree-empty">
            <p>Loading organizational structure...</p>
          </div>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="organization-tree">
        <div className="tree-header">
          <h3>Organization Structure</h3>
        </div>
        <div className="tree-content">
          <div className="tree-empty">
            <p style={{ color: '#dc3545' }}>{error}</p>
            <button
              onClick={() => window.location.reload()}
              style={{
                marginTop: '8px',
                padding: '4px 8px',
                background: '#007bff',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="organization-tree">
      <div className="tree-header">
        <h3>Organization Structure {useApi && <span style={{ fontSize: '12px', color: '#666' }}>(API)</span>}</h3>
        <div className="tree-summary">
          <span className="summary-item">
            <strong>{Object.keys(organizationTree).length}</strong> Agencies
          </span>
          <span className="summary-item">
            <strong>{totalReviews}</strong> Reviews
          </span>
        </div>
      </div>

      <div className="tree-content">
        {Object.keys(organizationTree).length === 0 ? (
          <div className="tree-empty">
            <p>No organizational data available</p>
          </div>
        ) : (
          <div className="tree-nodes">
            {Object.values(organizationTree).map(agencyData =>
              renderAgency(agencyData)
            )}
          </div>
        )}
      </div>

      {/* Modal components for document actions */}
      {selectedDocumentId && (
        <DocumentDetails
          documentId={selectedDocumentId}
          onClose={handleCloseModal}
        />
      )}

      {selectedAuditDocumentId && (
        <AuditLog
          documentId={selectedAuditDocumentId}
          onClose={handleCloseAuditModal}
        />
      )}

      {selectedEditDocumentId && (
        <EditDocument
          documentId={selectedEditDocumentId}
          onClose={handleCloseEditModal}
          onDocumentUpdated={() => {
            // Refresh reviews list - in a real app, you'd want to refetch the data
            window.location.reload();
          }}
        />
      )}
    </div>
  );
};

export default OrganizationTree;